"use client";
import React from 'react';
import { useCopilotAction } from "@copilotkit/react-core";
import { LoadingView } from '../ui/CalendarMeetingCard';
import { Calendar, MapPin, Users, Clock, Check, AlertCircle, DollarSign, Star, Eye } from 'lucide-react';
// Mock resource database with available order items
const mockResources = [
    {
        id: 'r1',
        name: 'Conference Room A',
        description: 'Large conference room with video conferencing equipment',
        pricePerHour: 75.00,
        category: 'Conference Room',
        rating: 4.8,
        capacity: 12,
        amenities: ['Projector', 'Whiteboard', 'Video Conference', 'WiFi'],
        isAvailable: true,
        location: 'Building A, Floor 2',
        availableOrderItems: [
            { id: 'oi1', name: 'Coffee & Tea Service', description: 'Premium coffee and tea for all attendees', price: 15.00, category: 'Catering', unit: 'per person' },
            { id: 'oi2', name: 'AV Technical Support', description: 'On-site technical support for presentations', price: 50.00, category: 'Technical', unit: 'per hour' },
            { id: 'oi3', name: 'Lunch Catering', description: 'Business lunch for meeting participants', price: 25.00, category: 'Catering', unit: 'per person' },
            { id: 'oi4', name: 'Flip Chart Setup', description: 'Additional flip charts and markers', price: 20.00, category: 'Equipment', unit: 'fixed' }
        ]
    },
    {
        id: 'r2',
        name: 'Meeting Room B',
        description: 'Medium meeting room perfect for team discussions',
        pricePerHour: 45.00,
        category: 'Meeting Room',
        rating: 4.6,
        capacity: 8,
        amenities: ['TV Display', 'Whiteboard', 'WiFi'],
        isAvailable: true,
        location: 'Building A, Floor 3',
        availableOrderItems: [
            { id: 'oi5', name: 'Coffee Service', description: 'Basic coffee service', price: 10.00, category: 'Catering', unit: 'per person' },
            { id: 'oi6', name: 'Stationery Kit', description: 'Notebooks, pens, and basic supplies', price: 15.00, category: 'Equipment', unit: 'fixed' },
            { id: 'oi7', name: 'Video Call Setup', description: 'Teams/Zoom setup assistance', price: 30.00, category: 'Technical', unit: 'fixed' }
        ]
    },
    {
        id: 'r3',
        name: 'Executive Boardroom',
        description: 'Premium boardroom for executive meetings',
        pricePerHour: 120.00,
        category: 'Boardroom',
        rating: 4.9,
        capacity: 16,
        amenities: ['4K Display', 'Video Conference', 'Audio System', 'Catering Setup', 'WiFi'],
        isAvailable: true,
        location: 'Building B, Floor 5',
        availableOrderItems: [
            { id: 'oi8', name: 'Executive Catering', description: 'Premium catering with multiple courses', price: 45.00, category: 'Catering', unit: 'per person' },
            { id: 'oi9', name: 'Dedicated AV Technician', description: 'Full-time technical support', price: 80.00, category: 'Technical', unit: 'per hour' },
            { id: 'oi10', name: 'Welcome Reception', description: 'Pre-meeting reception setup', price: 200.00, category: 'Catering', unit: 'fixed' },
            { id: 'oi11', name: 'Live Streaming Setup', description: 'Professional live streaming equipment', price: 150.00, category: 'Technical', unit: 'fixed' }
        ]
    },
    {
        id: 'r4',
        name: 'Training Room C',
        description: 'Spacious training room with presentation setup',
        pricePerHour: 60.00,
        category: 'Training Room',
        rating: 4.5,
        capacity: 20,
        amenities: ['Projector', 'Sound System', 'Flip Charts', 'WiFi'],
        isAvailable: true,
        location: 'Building C, Floor 1',
        availableOrderItems: [
            { id: 'oi12', name: 'Training Materials', description: 'Printed handouts and materials', price: 8.00, category: 'Equipment', unit: 'per person' },
            { id: 'oi13', name: 'Break Refreshments', description: 'Coffee breaks with snacks', price: 12.00, category: 'Catering', unit: 'per person' },
            { id: 'oi14', name: 'Recording Service', description: 'Professional session recording', price: 100.00, category: 'Technical', unit: 'fixed' },
            { id: 'oi15', name: 'Interactive Whiteboard', description: 'Smart whiteboard rental', price: 40.00, category: 'Equipment', unit: 'per hour' }
        ]
    },
    {
        id: 'r5',
        name: 'Innovation Lab',
        description: 'Creative workspace with modern collaboration tools',
        pricePerHour: 90.00,
        category: 'Collaboration Space',
        rating: 4.7,
        capacity: 10,
        amenities: ['Interactive Whiteboard', 'Brainstorming Tools', 'Standing Desks', 'WiFi'],
        isAvailable: false,
        location: 'Building D, Floor 2',
        availableOrderItems: [
            { id: 'oi16', name: 'Creative Snacks', description: 'Brain food and energy snacks', price: 18.00, category: 'Catering', unit: 'per person' },
            { id: 'oi17', name: 'Design Thinking Kit', description: 'Post-its, markers, and ideation tools', price: 35.00, category: 'Equipment', unit: 'fixed' },
            { id: 'oi18', name: 'Innovation Facilitator', description: 'Professional facilitation service', price: 120.00, category: 'Service', unit: 'per hour' }
        ]
    }
];
// Helper function to generate order ID
const generateOrderId = () => {
    return 'ORD-' + Date.now().toString(36).toUpperCase();
};
// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount);
};
// Helper function to format meeting date
const formatMeetingDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};
// Helper function to format meeting time
const formatMeetingTime = (startString, endString) => {
    const start = new Date(startString);
    const end = new Date(endString);
    const startTime = start.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
    const endTime = end.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
    return `${startTime} - ${endTime}`;
};
// Helper function to check if we're in Office context
const isOfficeContext = () => {
    return !!(typeof Office !== 'undefined' &&
        Office.context &&
        Office.context.mailbox &&
        Office.context.mailbox.item);
};
export function ActionsBookingOrder() {
    const [currentOrder, setCurrentOrder] = React.useState(null);
    const [currentMeeting, setCurrentMeeting] = React.useState(null);
    // Get available resources
    useCopilotAction({
        name: "getAvailableResources",
        description: "Get list of available resources (meeting rooms, conference rooms) for booking with their available order items",
        parameters: [
            {
                name: "category",
                type: "string",
                description: "Filter by resource category (optional): 'Conference Room', 'Meeting Room', 'Boardroom', 'Training Room', 'Collaboration Space', or leave empty for all",
                required: false,
            },
        ],
        handler: async ({ category }) => {
            try {
                let filteredResources = mockResources.filter(r => r.isAvailable);
                if (category) {
                    filteredResources = filteredResources.filter(r => r.category.toLowerCase() === category.toLowerCase());
                }
                return {
                    _success: true,
                    resources: filteredResources,
                    totalResources: filteredResources.length,
                    categories: [...new Set(mockResources.map(r => r.category))],
                    locations: [...new Set(mockResources.map(r => r.location))],
                    _message: `Found ${filteredResources.length} available resources with order items`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to get resources: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.resources) {
                    return (React.createElement("div", { className: "bg-white border border-gray-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                            React.createElement(MapPin, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("h4", { className: "font-medium text-gray-900" },
                                "Available Resources (",
                                result.totalResources,
                                ")")),
                        React.createElement("div", { className: "space-y-4" }, result.resources.map((resource) => (React.createElement("div", { key: resource.id, className: "border border-gray-100 rounded-lg p-4" },
                            React.createElement("div", { className: "flex justify-between items-start mb-3" },
                                React.createElement("div", null,
                                    React.createElement("h5", { className: "font-medium text-gray-900" }, resource.name),
                                    React.createElement("p", { className: "text-sm text-gray-600" }, resource.description)),
                                React.createElement("span", { className: "text-lg font-bold text-blue-600" },
                                    formatCurrency(resource.pricePerHour),
                                    "/hr")),
                            React.createElement("div", { className: "grid grid-cols-2 gap-4 mb-3" },
                                React.createElement("div", { className: "flex items-center space-x-2" },
                                    React.createElement(Users, { className: "w-4 h-4 text-gray-500" }),
                                    React.createElement("span", { className: "text-sm text-gray-600" },
                                        "Capacity: ",
                                        resource.capacity,
                                        " people")),
                                React.createElement("div", { className: "flex items-center space-x-2" },
                                    React.createElement(MapPin, { className: "w-4 h-4 text-gray-500" }),
                                    React.createElement("span", { className: "text-sm text-gray-600" }, resource.location))),
                            React.createElement("div", { className: "flex flex-wrap gap-1 mb-3" }, resource.amenities.map((amenity, index) => (React.createElement("span", { key: index, className: "text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full" }, amenity)))),
                            React.createElement("div", { className: "border-t border-gray-100 pt-3" },
                                React.createElement("h6", { className: "font-medium text-gray-900 mb-2" }, "Available Order Items:"),
                                React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-2" }, resource.availableOrderItems.map((item) => (React.createElement("div", { key: item.id, className: "bg-gray-50 p-2 rounded text-sm" },
                                    React.createElement("div", { className: "flex justify-between items-start" },
                                        React.createElement("div", null,
                                            React.createElement("p", { className: "font-medium text-gray-800" }, item.name),
                                            React.createElement("p", { className: "text-xs text-gray-600" }, item.description)),
                                        React.createElement("div", { className: "text-right" },
                                            React.createElement("p", { className: "font-bold text-green-600" }, formatCurrency(item.price)),
                                            React.createElement("p", { className: "text-xs text-gray-500" }, item.unit))),
                                    React.createElement("span", { className: "text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full" }, item.category)))))),
                            React.createElement("div", { className: "flex justify-between items-center mt-3" },
                                React.createElement("span", { className: "text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full" }, resource.category),
                                React.createElement("div", { className: "flex items-center space-x-1" },
                                    React.createElement(Star, { className: "w-4 h-4 text-yellow-500 fill-current" }),
                                    React.createElement("span", { className: "text-sm text-gray-600" }, resource.rating))))))),
                        React.createElement("div", { className: "mt-4 p-3 bg-gray-50 rounded-lg" },
                            React.createElement("div", { className: "text-sm text-gray-700 space-y-1" },
                                React.createElement("div", null,
                                    React.createElement("strong", null, "Categories:"),
                                    " ",
                                    result.categories.join(', ')),
                                React.createElement("div", null,
                                    React.createElement("strong", null, "Locations:"),
                                    " ",
                                    result.locations.join(', '))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Create order with resources and order items
    useCopilotAction({
        name: "createOrderWithResources",
        description: "Create a booking order with selected resources and additional order items (services)",
        parameters: [
            {
                name: "customerEmail",
                type: "string",
                description: "Customer email address for the booking",
                required: true,
            },
            {
                name: "resourceBookings",
                type: "string",
                description: "JSON string with array of resource bookings. Each booking should have: resourceId, bookingDate (YYYY-MM-DD), startTime (HH:MM), endTime (HH:MM), orderItems (array of {orderItemId, quantity}). Example: '[{\"resourceId\":\"r1\",\"bookingDate\":\"2025-01-20\",\"startTime\":\"14:00\",\"endTime\":\"16:00\",\"orderItems\":[{\"orderItemId\":\"oi1\",\"quantity\":12}]}]'",
                required: true,
            },
        ],
        handler: async ({ customerEmail, resourceBookings }) => {
            try {
                const bookings = JSON.parse(resourceBookings);
                if (!Array.isArray(bookings) || bookings.length === 0) {
                    return {
                        _error: true,
                        _message: "Please provide at least one resource booking"
                    };
                }
                const resourceBookingList = [];
                let totalAmount = 0;
                for (const booking of bookings) {
                    const resource = mockResources.find(r => r.id === booking.resourceId);
                    if (!resource) {
                        return {
                            _error: true,
                            _message: `Resource not found: ${booking.resourceId}`
                        };
                    }
                    if (!resource.isAvailable) {
                        return {
                            _error: true,
                            _message: `Resource not available: ${resource.name}`
                        };
                    }
                    // Calculate duration
                    const startTime = new Date(`${booking.bookingDate} ${booking.startTime}`);
                    const endTime = new Date(`${booking.bookingDate} ${booking.endTime}`);
                    const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60); // hours
                    if (duration <= 0) {
                        return {
                            _error: true,
                            _message: `Invalid booking time for ${resource.name}: End time must be after start time`
                        };
                    }
                    const resourceTotal = resource.pricePerHour * duration;
                    // Process order items
                    const orderItems = [];
                    let orderItemsTotal = 0;
                    if (booking.orderItems && Array.isArray(booking.orderItems)) {
                        for (const orderItemRequest of booking.orderItems) {
                            const orderItemType = resource.availableOrderItems.find(oi => oi.id === orderItemRequest.orderItemId);
                            if (!orderItemType) {
                                return {
                                    _error: true,
                                    _message: `Order item not found: ${orderItemRequest.orderItemId} for resource ${resource.name}`
                                };
                            }
                            let itemTotal = 0;
                            const quantity = orderItemRequest.quantity || 1;
                            // Calculate based on unit type
                            switch (orderItemType.unit) {
                                case 'per hour':
                                    itemTotal = orderItemType.price * quantity * duration;
                                    break;
                                case 'per person':
                                    itemTotal = orderItemType.price * quantity;
                                    break;
                                case 'fixed':
                                    itemTotal = orderItemType.price * quantity;
                                    break;
                                default:
                                    itemTotal = orderItemType.price * quantity;
                            }
                            orderItems.push({
                                orderItemId: orderItemType.id,
                                orderItemName: orderItemType.name,
                                quantity,
                                price: orderItemType.price,
                                total: itemTotal,
                                unit: orderItemType.unit
                            });
                            orderItemsTotal += itemTotal;
                        }
                    }
                    const grandTotal = resourceTotal + orderItemsTotal;
                    totalAmount += grandTotal;
                    resourceBookingList.push({
                        resourceId: resource.id,
                        resourceName: resource.name,
                        duration,
                        pricePerHour: resource.pricePerHour,
                        bookingDate: booking.bookingDate,
                        startTime: booking.startTime,
                        endTime: booking.endTime,
                        location: resource.location,
                        resourceTotal,
                        orderItems,
                        orderItemsTotal,
                        grandTotal
                    });
                }
                const newOrder = {
                    id: generateOrderId(),
                    bookings: resourceBookingList,
                    totalAmount,
                    customerEmail,
                    status: 'pending',
                    createdAt: new Date().toISOString()
                };
                setCurrentOrder(newOrder);
                return {
                    _success: true,
                    order: newOrder,
                    _message: `Order created successfully! Total: ${formatCurrency(totalAmount)}`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to create order: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.order) {
                    const order = result.order;
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("h4", { className: "font-medium text-green-900" }, "\u2705 Booking Order Created!")),
                        React.createElement("div", { className: "space-y-3" },
                            React.createElement("div", { className: "flex justify-between items-center p-3 bg-white rounded-lg border" },
                                React.createElement("div", null,
                                    React.createElement("p", { className: "font-medium text-gray-900" },
                                        "Order ID: ",
                                        order.id),
                                    React.createElement("p", { className: "text-sm text-gray-600" },
                                        "Customer: ",
                                        order.customerEmail),
                                    React.createElement("p", { className: "text-sm text-gray-600" },
                                        "Status: ",
                                        React.createElement("span", { className: "capitalize" }, order.status))),
                                React.createElement("div", { className: "text-right" },
                                    React.createElement("p", { className: "text-2xl font-bold text-green-600" }, formatCurrency(order.totalAmount)),
                                    React.createElement("p", { className: "text-sm text-gray-600" }, "Total"))),
                            React.createElement("div", { className: "space-y-3" },
                                React.createElement("h5", { className: "font-medium text-gray-900" }, "Resource Bookings:"),
                                order.bookings.map((booking, index) => (React.createElement("div", { key: index, className: "p-4 bg-white rounded-lg border border-gray-100" },
                                    React.createElement("div", { className: "flex justify-between items-start mb-3" },
                                        React.createElement("h6", { className: "font-medium text-gray-900" }, booking.resourceName),
                                        React.createElement("span", { className: "text-lg font-bold text-blue-600" }, formatCurrency(booking.grandTotal))),
                                    React.createElement("div", { className: "grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3" },
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(Calendar, { className: "w-4 h-4" }),
                                            React.createElement("span", null, formatMeetingDate(booking.bookingDate))),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(Clock, { className: "w-4 h-4" }),
                                            React.createElement("span", null,
                                                booking.startTime,
                                                " - ",
                                                booking.endTime,
                                                " (",
                                                booking.duration,
                                                "h)")),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(MapPin, { className: "w-4 h-4" }),
                                            React.createElement("span", null, booking.location)),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(DollarSign, { className: "w-4 h-4" }),
                                            React.createElement("span", null,
                                                formatCurrency(booking.pricePerHour),
                                                "/hour"))),
                                    React.createElement("div", { className: "border-t border-gray-100 pt-3" },
                                        React.createElement("div", { className: "flex justify-between text-sm mb-2" },
                                            React.createElement("span", null, "Resource Cost:"),
                                            React.createElement("span", null, formatCurrency(booking.resourceTotal))),
                                        booking.orderItems.length > 0 && (React.createElement(React.Fragment, null,
                                            React.createElement("div", { className: "text-sm font-medium mb-2" }, "Order Items:"),
                                            booking.orderItems.map((item, itemIndex) => (React.createElement("div", { key: itemIndex, className: "flex justify-between text-sm text-gray-600 mb-1" },
                                                React.createElement("span", null,
                                                    item.orderItemName,
                                                    " (\u00D7",
                                                    item.quantity,
                                                    " ",
                                                    item.unit,
                                                    ")"),
                                                React.createElement("span", null, formatCurrency(item.total))))),
                                            React.createElement("div", { className: "flex justify-between text-sm mb-2" },
                                                React.createElement("span", null, "Order Items Total:"),
                                                React.createElement("span", null, formatCurrency(booking.orderItemsTotal))))),
                                        React.createElement("div", { className: "flex justify-between font-medium border-t border-gray-100 pt-2" },
                                            React.createElement("span", null, "Booking Total:"),
                                            React.createElement("span", { className: "text-blue-600" }, formatCurrency(booking.grandTotal)))))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Book meeting with order
    useCopilotAction({
        name: "bookMeetingWithOrder",
        description: "Create a meeting appointment in Outlook with the current resource booking order",
        parameters: [
            {
                name: "meetingSubject",
                type: "string",
                description: "Subject/title for the meeting",
                required: true,
            },
            {
                name: "attendees",
                type: "string",
                description: "Email addresses of attendees, comma-separated (e.g., 'john@company.com, jane@company.com')",
                required: true,
            },
            {
                name: "meetingType",
                type: "string",
                description: "Type of meeting: 'resource_booking', 'meeting_setup', 'facility_tour', or 'space_consultation'",
                required: true,
            },
        ],
        handler: async ({ meetingSubject, attendees, meetingType }) => {
            try {
                if (!currentOrder) {
                    return {
                        _error: true,
                        _message: "No current order found. Please create a resource booking order first."
                    };
                }
                if (!isOfficeContext()) {
                    return {
                        _error: true,
                        _message: "This action only works within Outlook context"
                    };
                }
                // Use the first booking's details for the meeting
                const firstBooking = currentOrder.bookings[0];
                const startDateTime = new Date(`${firstBooking.bookingDate} ${firstBooking.startTime}`);
                const endDateTime = new Date(`${firstBooking.bookingDate} ${firstBooking.endTime}`);
                const meetingWithOrder = {
                    meetingSubject,
                    startTime: startDateTime.toISOString(),
                    endTime: endDateTime.toISOString(),
                    location: firstBooking.location,
                    attendees: attendees.split(',').map(email => email.trim()),
                    order: currentOrder,
                    meetingType: meetingType
                };
                setCurrentMeeting(meetingWithOrder);
                // Set meeting details in Outlook
                return new Promise((resolve) => {
                    const item = Office.context.mailbox.item;
                    if (!item) {
                        resolve({
                            _error: true,
                            _message: "No appointment item found in Outlook"
                        });
                        return;
                    }
                    let completed = 0;
                    const totalOperations = 4;
                    let hasError = false;
                    const checkComplete = () => {
                        completed++;
                        if (completed === totalOperations && !hasError) {
                            resolve({
                                _success: true,
                                meeting: meetingWithOrder,
                                _message: `Meeting "${meetingSubject}" created with resource booking order ${currentOrder.id}`
                            });
                        }
                    };
                    // Set subject
                    item.subject.setAsync(meetingSubject, (result) => {
                        var _a;
                        if (result.status !== Office.AsyncResultStatus.Succeeded && !hasError) {
                            hasError = true;
                            resolve({
                                _error: true,
                                _message: `Failed to set meeting subject: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                            });
                        }
                        else {
                            checkComplete();
                        }
                    });
                    // Set location
                    item.location.setAsync(firstBooking.location, (result) => {
                        var _a;
                        if (result.status !== Office.AsyncResultStatus.Succeeded && !hasError) {
                            hasError = true;
                            resolve({
                                _error: true,
                                _message: `Failed to set meeting location: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                            });
                        }
                        else {
                            checkComplete();
                        }
                    });
                    // Set start time
                    item.start.setAsync(startDateTime, (result) => {
                        var _a;
                        if (result.status !== Office.AsyncResultStatus.Succeeded && !hasError) {
                            hasError = true;
                            resolve({
                                _error: true,
                                _message: `Failed to set start time: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                            });
                        }
                        else {
                            checkComplete();
                        }
                    });
                    // Set end time
                    item.end.setAsync(endDateTime, (result) => {
                        var _a;
                        if (result.status !== Office.AsyncResultStatus.Succeeded && !hasError) {
                            hasError = true;
                            resolve({
                                _error: true,
                                _message: `Failed to set end time: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                            });
                        }
                        else {
                            checkComplete();
                        }
                    });
                });
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to create meeting: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.meeting) {
                    const meeting = result.meeting;
                    return (React.createElement("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                            React.createElement(Check, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("h4", { className: "font-medium text-blue-900" }, "\u2705 Meeting Created with Resource Booking!")),
                        React.createElement("div", { className: "space-y-3" },
                            React.createElement("div", { className: "p-3 bg-white rounded-lg border" },
                                React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3" },
                                    React.createElement("div", null,
                                        React.createElement("h5", { className: "font-medium text-gray-900 mb-2" }, "Meeting Details"),
                                        React.createElement("div", { className: "space-y-1 text-sm text-gray-600" },
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Subject:"),
                                                " ",
                                                meeting.meetingSubject),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Type:"),
                                                " ",
                                                meeting.meetingType.replace('_', ' ').toUpperCase()),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Date:"),
                                                " ",
                                                formatMeetingDate(meeting.startTime)),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Time:"),
                                                " ",
                                                formatMeetingTime(meeting.startTime, meeting.endTime)),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Location:"),
                                                " ",
                                                meeting.location))),
                                    React.createElement("div", null,
                                        React.createElement("h5", { className: "font-medium text-gray-900 mb-2" }, "Booking Summary"),
                                        React.createElement("div", { className: "space-y-1 text-sm text-gray-600" },
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Order ID:"),
                                                " ",
                                                meeting.order.id),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Resources:"),
                                                " ",
                                                meeting.order.bookings.length),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Total Cost:"),
                                                " ",
                                                formatCurrency(meeting.order.totalAmount)),
                                            React.createElement("p", null,
                                                React.createElement("strong", null, "Status:"),
                                                " ",
                                                React.createElement("span", { className: "capitalize" }, meeting.order.status)))))),
                            React.createElement("div", { className: "p-3 bg-white rounded-lg border" },
                                React.createElement("h5", { className: "font-medium text-gray-900 mb-2" }, "Attendees"),
                                React.createElement("div", { className: "flex flex-wrap gap-2" }, meeting.attendees.map((email, index) => (React.createElement("span", { key: index, className: "px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm" }, email))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Get current order status
    useCopilotAction({
        name: "getCurrentOrderStatus",
        description: "Get the status and details of the current resource booking order",
        parameters: [],
        handler: async () => {
            try {
                if (!currentOrder) {
                    return {
                        _error: true,
                        _message: "No current order found. Please create a resource booking order first."
                    };
                }
                return {
                    _success: true,
                    order: currentOrder,
                    meeting: currentMeeting,
                    _message: `Current order ${currentOrder.id} has ${currentOrder.bookings.length} resource booking(s) for ${formatCurrency(currentOrder.totalAmount)}`
                };
            }
            catch (error) {
                return {
                    _error: true,
                    _message: `Failed to get order status: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-yellow-600" }),
                            React.createElement("p", { className: "text-yellow-800" }, result._message))));
                }
                if (result._success && result.order) {
                    const order = result.order;
                    const meeting = result.meeting;
                    return (React.createElement("div", { className: "bg-gray-50 border border-gray-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                            React.createElement(Eye, { className: "w-5 h-5 text-gray-600" }),
                            React.createElement("h4", { className: "font-medium text-gray-900" }, "Current Resource Booking Status")),
                        React.createElement("div", { className: "space-y-3" },
                            React.createElement("div", { className: "p-3 bg-white rounded-lg border" },
                                React.createElement("div", { className: "flex justify-between items-center mb-2" },
                                    React.createElement("h5", { className: "font-medium text-gray-900" },
                                        "Order ",
                                        order.id),
                                    React.createElement("span", { className: `px-2 py-1 rounded-full text-sm ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                                'bg-yellow-100 text-yellow-800'}` }, order.status.toUpperCase())),
                                React.createElement("div", { className: "grid grid-cols-2 gap-4 text-sm text-gray-600" },
                                    React.createElement("div", null,
                                        React.createElement("p", null,
                                            React.createElement("strong", null, "Customer:"),
                                            " ",
                                            order.customerEmail),
                                        React.createElement("p", null,
                                            React.createElement("strong", null, "Created:"),
                                            " ",
                                            new Date(order.createdAt).toLocaleDateString())),
                                    React.createElement("div", { className: "text-right" },
                                        React.createElement("p", { className: "text-2xl font-bold text-blue-600" }, formatCurrency(order.totalAmount)),
                                        React.createElement("p", { className: "text-sm text-gray-500" }, "Total Amount")))),
                            meeting && (React.createElement("div", { className: "p-3 bg-white rounded-lg border" },
                                React.createElement("h5", { className: "font-medium text-gray-900 mb-2" }, "Associated Meeting"),
                                React.createElement("div", { className: "text-sm text-gray-600" },
                                    React.createElement("p", null,
                                        React.createElement("strong", null, "Subject:"),
                                        " ",
                                        meeting.meetingSubject),
                                    React.createElement("p", null,
                                        React.createElement("strong", null, "Date:"),
                                        " ",
                                        formatMeetingDate(meeting.startTime)),
                                    React.createElement("p", null,
                                        React.createElement("strong", null, "Time:"),
                                        " ",
                                        formatMeetingTime(meeting.startTime, meeting.endTime)),
                                    React.createElement("p", null,
                                        React.createElement("strong", null, "Location:"),
                                        " ",
                                        meeting.location)))),
                            React.createElement("div", { className: "space-y-2" },
                                React.createElement("h5", { className: "font-medium text-gray-900" },
                                    "Resource Bookings (",
                                    order.bookings.length,
                                    ")"),
                                order.bookings.map((booking, index) => (React.createElement("div", { key: index, className: "p-3 bg-white rounded-lg border border-gray-100" },
                                    React.createElement("div", { className: "flex justify-between items-start mb-2" },
                                        React.createElement("h6", { className: "font-medium text-gray-900" }, booking.resourceName),
                                        React.createElement("span", { className: "text-lg font-bold text-blue-600" }, formatCurrency(booking.grandTotal))),
                                    React.createElement("div", { className: "grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2" },
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(Calendar, { className: "w-4 h-4" }),
                                            React.createElement("span", null, formatMeetingDate(booking.bookingDate))),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(Clock, { className: "w-4 h-4" }),
                                            React.createElement("span", null,
                                                booking.startTime,
                                                " - ",
                                                booking.endTime)),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(MapPin, { className: "w-4 h-4" }),
                                            React.createElement("span", null, booking.location)),
                                        React.createElement("div", { className: "flex items-center space-x-1" },
                                            React.createElement(DollarSign, { className: "w-4 h-4" }),
                                            React.createElement("span", null,
                                                booking.duration,
                                                "h \u00D7 ",
                                                formatCurrency(booking.pricePerHour),
                                                "/hr"))),
                                    booking.orderItems.length > 0 && (React.createElement("div", { className: "border-t border-gray-100 pt-2" },
                                        React.createElement("p", { className: "text-xs font-medium text-gray-700 mb-1" }, "Order Items:"),
                                        booking.orderItems.map((item, itemIndex) => (React.createElement("div", { key: itemIndex, className: "flex justify-between text-xs text-gray-600" },
                                            React.createElement("span", null,
                                                item.orderItemName,
                                                " (\u00D7",
                                                item.quantity,
                                                ")"),
                                            React.createElement("span", null, formatCurrency(item.total))))))))))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    return null; // This component doesn't render anything directly
}
